import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";

export function useAccessPendingAction(scope: string) {
  const intl = useIntl();
  const [action, setAction] = useState<{ label: string; run: () => Promise<void> } | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  usePendingScope(scope, {
    dirty: !!action,
    save() {
      if (inFlight.current) return inFlight.current;
      if (!action) return;
      const pending = action.run().then(() => setAction(null)).catch(cause => {
        throw new LocalizedError(cause instanceof LocalizedError ? cause.message : userErrorMessage(intl, cause));
      }).finally(() => { inFlight.current = null; });
      inFlight.current = pending;
      return pending;
    },
    discard() { setAction(null); },
  });
  return {
    pending: !!action,
    label: action ? intl.formatMessage({ id: "admin.access.pendingAction" }, { action: action.label }) : null,
    stage(label: string, run: () => Promise<void>) { if (!action) setAction({ label, run }); },
  };
}
